// @flow

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import LogoutIcon from '@mui/icons-material/PowerSettingsNew';
import UserIcon from '@mui/icons-material/AccountCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';

import SPBackend from '../services/SPBackend';
import { log } from '../utils/jsUtils';

import Logout from './Logout';
import memberHeaderStyles from './Feed/Styles/MemberHeader.Style';
import {getUserRole, ROLE_NAMES} from '../models/User';

// Import selectors
import { selectUser, selectUserFullName, selectAccounts, selectUserRole, selectAccountId, selectCurrentAccountCompanyName } from '../reduxStore/modules/user/selectors';
import { setSnackMessage } from '../reduxStore/modules/common';

type Props = {
  isOnboading?: boolean // user is still onboarding, limit options.
}

const UserProfileMenu = (props: Props) => {
  const {
    isOnboading
  } = props;
  
  // Replace useStore with Redux selectors
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const userFullName = useSelector(selectUserFullName);
  const accounts = useSelector(selectAccounts);
  const userRole = useSelector(selectUserRole);
  const accountId = useSelector(selectAccountId);
  // Get company name from accounts
  const companyName = useSelector(selectCurrentAccountCompanyName);
  
  const [isUserOpen, setUserOpen] = useState(false);
  const [loadingAccountSwitch, setLoadingAccountSwitch] = useState(false);

  const theme = useTheme();

  const handleClickAway = () => {
    setUserOpen(false);
  };
  
  const switchAccount = (accountId: string) => async() => {
    setLoadingAccountSwitch(true);
    try {
      const { data } = await SPBackend.switchAccount(user, accountId);
      log('[switchOrganization] data: ', data);

      if (data.message === 'ok') {
        log('[switchOrganization] switching!!');
        window.location.href = '/member';
      } else {
        setLoadingAccountSwitch(false);
        dispatch(setSnackMessage({
          message: data.message,
          level: 'error',
          duration: 5000
        }));
      }
    } catch (error) {
      console.error('[switchOrganization] error: ', error);
      setLoadingAccountSwitch(false);
      dispatch(setSnackMessage({
        message: 'Something went wrong.',
        level: 'error',
        duration: 5000
      }));
    }
  };


  return (
    <Grid 
      sx={{
        height: '100%',
        minWidth: 120,
        right: 0,
        '& a': {
          textDecoration: 'none'
        },
        '& *': {
          color: 'white'
        }
      }}
    >
      <List disablePadding>
      <ClickAwayListener onClickAway={handleClickAway}>
        <ListItem
          button
          sx={(theme) => memberHeaderStyles.listItem(theme)}
          key={'UserAccount'}
          onClick={() => setUserOpen(!isUserOpen)}
        >
          <ListItemText
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                overflow: 'hidden',
                maxWidth: '30vw',
                '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                  whiteSpace: 'nowrap',
                  overflowX: 'scroll'
                }
              }
            })}
            primary={userFullName}
            secondary={<React.Fragment>
              <span>{`@${companyName}`}</span>
              <br />
              {
                process.env.REACT_APP_ENVF !== 'env.production'
                ? `${accountId}`
                : null
              }
            </React.Fragment>}
          />
          {isUserOpen ? <ExpandLess /> : <ExpandMore />}
          <ListItemIcon sx={(theme) => memberHeaderStyles.iconRoot(theme)}><UserIcon /></ListItemIcon>
        </ListItem>
        </ClickAwayListener>

        <Collapse
          sx={(theme) => memberHeaderStyles.userProfileSubMenuCollapse(theme)}
          className={'coloring'}
          in={isUserOpen}
          timeout="auto"
          unmountOnExit
        >
          <Link to="/logout">
            <ListItem
              button
              sx={(theme) => memberHeaderStyles.subListItem(theme)}
              key={'Logout'}
            >
              <ListItemText
                sx={(theme) => memberHeaderStyles.itemTextCtr(theme)}
                primary={'Logout'}
              />
              <ListItemIcon sx={(theme) => memberHeaderStyles.iconRoot(theme)}>
                <LogoutIcon />
              </ListItemIcon>
            </ListItem>
          </Link>
          <Divider />
        {
          accounts && accounts.length > 1
          ? [
            <ListSubheader
              key='accountheader'
              sx={(theme) => ({
                ...memberHeaderStyles.switchActHeader(theme),
              })}
            >{'Switch Account'}</ListSubheader>,
            ...accounts.map((act, actIdx) => (
              act._id !== accountId
              ? <ListItem
                  button
                  sx={(theme) => memberHeaderStyles.subListItem(theme)}
                  key={`account-${actIdx}`}
                  onClick={switchAccount(act._id)}
                >
                  <ListItemText
                    sx={(theme) => memberHeaderStyles.itemTextCtr(theme)}
                    primary={`@${act.CompanyName}`}
                  />
                  <ListItemIcon sx={(theme) => memberHeaderStyles.iconRoot(theme)}><UserIcon /></ListItemIcon>
                </ListItem>
              : null
            ))
          ]
          : null
        }
        </Collapse>
      </List>
    </Grid>
  );
};

export default UserProfileMenu;
